/**
 * Upload Wolt settlement to voucher queue, replicating the old Wolt edge function logic.
 *
 * Creates TWO vouchers:
 *   Voucher A (revenue month): Revenue (credit) + Fee buckets (debit) per venue,
 *     split by VAT rate (25%/15%), balanced by a swap/clearing account line.
 *   Voucher B (next month): Bank receipt (debit 1920) + Reverse clearing (credit swap).
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../../../lib/supabase';
import { WOLT_TYPES, WOLT_TYPE_LABELS } from './woltSettlementValidator';

const STORAGE_BUCKET = 'voucher_attachments';

// Tripletex entity IDs (from the old edge function)
const TRIPLETEX_BANK_1920 = '305878397';
const TRIPLETEX_REV_25 = '17171767';
const TRIPLETEX_REV_15 = '17171768';
const TRIPLETEX_SWAP = '17171723';
const SWAP_SUPPLIER_ID = 72009654;

const VAT_25_ID = 3;
const VAT_15_ID = 31;

const round2 = (n) => Math.round(n * 100) / 100;

/** Infer VAT type from net/vat ratio (matching old edge function) */
function inferVatTypeId(net, vat) {
  if (net === 0) return VAT_25_ID;
  const r = Math.abs(vat / net);
  return Math.abs(r - 0.25) <= 0.05 ? VAT_25_ID : VAT_15_ID;
}

function formatDateForDescription(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function formatAmountForPdf(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '–';
  const num = Number(n);
  const opts = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const formatted =
    num < 0
      ? `(${Math.abs(num).toLocaleString('nb-NO', opts)})`
      : num.toLocaleString('nb-NO', opts);
  return formatted.replace('.', ',');
}

function randomPdfFilename() {
  const t = Date.now();
  const r = Math.random().toString(36).slice(2, 11);
  return `wolt-${t}-${r}.pdf`;
}

function buildSettlementPdfBlob({ description, voucherDate, pivot, venueByName }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(14);
  doc.text(description, 14, 14);
  doc.setFontSize(10);
  doc.text(`Voucher date: ${voucherDate}`, 14, 21);

  const tableBody = (pivot.rows || []).map((venue) => [
    String(venue),
    venueByName[venue]?.department_name ?? '',
    ...WOLT_TYPES.map((type) => formatAmountForPdf(pivot.matrix.get(venue)?.get(type))),
  ]);

  autoTable(doc, {
    head: [['Venue Name', 'Department', ...WOLT_TYPES.map((t) => WOLT_TYPE_LABELS[t])]],
    body: tableBody,
    startY: 26,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [100, 100, 100] },
  });

  return doc.output('blob');
}

export function buildWoltSettlementDescription(data) {
  const dates = (data || [])
    .flatMap((row) => [row.timeFrameStartDate, row.timeFrameEndDate])
    .filter((d) => d != null && !isNaN(new Date(d).getTime()));
  if (dates.length === 0) return 'Wolt settlement';
  const first = new Date(Math.min(...dates.map((d) => new Date(d).getTime())));
  const last = new Date(Math.max(...dates.map((d) => new Date(d).getTime())));
  const firstStr = formatDateForDescription(first);
  const lastStr = formatDateForDescription(last);
  if (firstStr && lastStr) return `Wolt settlement from ${firstStr} to ${lastStr}`;
  return 'Wolt settlement';
}

/**
 * Look up tripletex_accounts rows by their tripletex_id values.
 * Returns a map: tripletex_id (string) → internal id (number).
 */
async function resolveAccountIds(tripletexIds) {
  const { data: allAccounts, error } = await supabase
    .from('tripletex_accounts')
    .select('id, tripletex_id');

  if (error) {
    throw new Error(`Failed to fetch tripletex_accounts: ${error.message}`);
  }

  const map = {};
  for (const a of allAccounts || []) {
    map[String(a.tripletex_id)] = a.id;
  }

  const missing = tripletexIds.filter((tid) => map[tid] == null);
  if (missing.length > 0) {
    throw new Error(
      `Missing tripletex_accounts entries for Tripletex IDs: ${missing.join(', ')}. ` +
      `Please add these accounts to the tripletex_accounts table.`
    );
  }

  return map;
}

/**
 * Aggregate settlement data per venue, splitting revenue and fees by VAT rate.
 * Matches the old edge function's perVenue aggregation logic.
 */
function aggregatePerVenue(settlementData, venueByName) {
  const perVenue = {};
  let endDateISO = '1970-01-01';

  for (const row of settlementData) {
    const venue = row.venueName || '';
    if (!venue) continue;

    const venueInfo = venueByName[venue];
    if (!venueInfo?.id) continue;

    if (!perVenue[venue]) {
      perVenue[venue] = {
        revenue: { [VAT_25_ID]: 0, [VAT_15_ID]: 0 },
        fees: { [VAT_25_ID]: 0, [VAT_15_ID]: 0 },
        venueId: venueInfo.id,
        departmentName: venueInfo.department_name || venue,
      };
    }

    // Revenue: use net/vat/gross goods sold
    const revGross = row.revenueGross ?? row.totalGoodsSold ?? 0;
    const revNet = row.revenueNet ?? 0;
    const revVat = row.revenueVat ?? 0;
    perVenue[venue].revenue[inferVatTypeId(revNet, revVat)] += revGross;

    // Fee buckets: each has net, vat, gross
    for (const bucket of row.feeBuckets || []) {
      if (bucket.gross !== 0) {
        perVenue[venue].fees[inferVatTypeId(bucket.net, bucket.vat)] += bucket.gross;
      }
    }

    // Track latest end date for voucher date
    if (row.timeFrameEndDate) {
      const d = formatDateForDescription(row.timeFrameEndDate);
      if (d && d > endDateISO) endDateISO = d;
    }
  }

  return { perVenue, endDateISO };
}

/**
 * Upload settlement to voucher tables. Creates two voucher uploads:
 *   A) Revenue voucher (end date of report period)
 *   B) Bank settlement voucher (first of next month)
 */
export async function uploadWoltSettlementToVoucher({ pivot, venueByName, settlementData }) {
  try {
    const accountMap = await resolveAccountIds([
      TRIPLETEX_REV_25,
      TRIPLETEX_REV_15,
      TRIPLETEX_SWAP,
      TRIPLETEX_BANK_1920,
    ]);

    const ACCOUNT_REV_25 = accountMap[TRIPLETEX_REV_25];
    const ACCOUNT_REV_15 = accountMap[TRIPLETEX_REV_15];
    const ACCOUNT_SWAP = accountMap[TRIPLETEX_SWAP];
    const ACCOUNT_BANK = accountMap[TRIPLETEX_BANK_1920];

    const accountForVat = (vatTypeId) =>
      vatTypeId === VAT_15_ID ? ACCOUNT_REV_15 : ACCOUNT_REV_25;

    const description = buildWoltSettlementDescription(settlementData);
    const { perVenue, endDateISO } = aggregatePerVenue(settlementData, venueByName);

    const voucherDateA = endDateISO !== '1970-01-01'
      ? endDateISO
      : (formatDateForDescription(new Date()) || new Date().toISOString().slice(0, 10));

    // Voucher B date: first of next month
    const nextMonth = new Date(voucherDateA + 'T00:00:00Z');
    nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1, 1);
    const voucherDateB = nextMonth.toISOString().slice(0, 10);

    // Generate and upload PDF
    const pdfBlob = buildSettlementPdfBlob({ description, voucherDate: voucherDateA, pivot, venueByName });
    const attachmentFilename = randomPdfFilename();
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(attachmentFilename, pdfBlob, { contentType: 'application/pdf', upsert: false });

    if (storageError) {
      throw new Error(`Failed to upload PDF to storage: ${storageError.message}`);
    }

    // ── Voucher A: Revenue + Fees + Swap balancing ─────────────────────

    const { data: voucherA, error: errA } = await supabase
      .from('voucher_uploads_to_tripletex')
      .insert({
        description: `Wolt Payout - ${description}`,
        voucher_date: voucherDateA,
        attachment: attachmentFilename,
      })
      .select('id')
      .single();

    if (errA || !voucherA?.id) {
      throw new Error(`Failed to create voucher A: ${errA?.message || 'no id returned'}`);
    }

    const linesA = [];
    let runningSum = 0;

    for (const [venue, agg] of Object.entries(perVenue)) {
      for (const vt of [VAT_25_ID, VAT_15_ID]) {
        if (agg.revenue[vt] !== 0) {
          const val = -round2(Math.abs(agg.revenue[vt])); // Credit
          runningSum += val;
          linesA.push({
            voucher_uploads_to_tripletex: voucherA.id,
            line_description: `Wolt Revenue - ${agg.departmentName}`,
            amount: val,
            account: accountForVat(vt),
            department: agg.venueId,
            supplier: null,
            customer: null,
            vatType: vt,
          });
        }
        if (agg.fees[vt] !== 0) {
          const val = round2(Math.abs(agg.fees[vt])); // Debit
          runningSum += val;
          linesA.push({
            voucher_uploads_to_tripletex: voucherA.id,
            line_description: `Wolt Fees - ${agg.departmentName}`,
            amount: val,
            account: accountForVat(vt),
            department: agg.venueId,
            supplier: null,
            customer: null,
            vatType: vt,
          });
        }
      }
    }

    // Balancing line on swap account (makes Voucher A sum to zero)
    const balancingAmount = -round2(runningSum);
    linesA.push({
      voucher_uploads_to_tripletex: voucherA.id,
      line_description: 'Payout clearing balancing line',
      amount: balancingAmount,
      account: ACCOUNT_SWAP,
      department: null,
      supplier: SWAP_SUPPLIER_ID,
      customer: null,
    });

    if (linesA.length > 0) {
      const { error: linesAErr } = await supabase
        .from('voucher_lines_uploads_to_tripletex')
        .insert(linesA);
      if (linesAErr) {
        throw new Error(`Failed to insert voucher A lines: ${linesAErr.message}`);
      }
    }

    // ── Voucher B: Bank settlement (next month) ────────────────────────

    const bankAmount = Math.abs(balancingAmount);

    const { data: voucherB, error: errB } = await supabase
      .from('voucher_uploads_to_tripletex')
      .insert({
        description: `Wolt Bank Settlement - ${description}`,
        voucher_date: voucherDateB,
      })
      .select('id')
      .single();

    if (errB || !voucherB?.id) {
      throw new Error(`Failed to create voucher B: ${errB?.message || 'no id returned'}`);
    }

    const linesB = [
      {
        voucher_uploads_to_tripletex: voucherB.id,
        line_description: 'Bank receipt',
        amount: bankAmount,
        account: ACCOUNT_BANK,
        department: null,
        supplier: null,
        customer: null,
      },
      {
        voucher_uploads_to_tripletex: voucherB.id,
        line_description: 'Reverse clearing',
        amount: -bankAmount,
        account: ACCOUNT_SWAP,
        department: null,
        supplier: SWAP_SUPPLIER_ID,
        customer: null,
      },
    ];

    const { error: linesBErr } = await supabase
      .from('voucher_lines_uploads_to_tripletex')
      .insert(linesB);

    if (linesBErr) {
      throw new Error(`Failed to insert voucher B lines: ${linesBErr.message}`);
    }

    return {
      success: true,
      voucherUploadIdA: voucherA.id,
      voucherUploadIdB: voucherB.id,
    };
  } catch (err) {
    return { success: false, error: err.message };
  }
}
